import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchServicesThunk, activateServicesThunk, deleteServicesThunk } from 'actions/services/ServicesActions';
import {
    getGroomingServices,
    getGroomingMeta,
    getGroomingLoading,
    getGroomingHasInitialized,
    getGroomingIsTrulyEmpty,
    getGroomingHasEverHadServices
} from 'reducers/services/ServicesSelectors';
import { Service } from 'model/Service';

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE = 1;

export const useGroomingServices = () => {
    const dispatch = useDispatch();

    // Use selectors specific to grooming (includes dental)
    const services = useSelector(getGroomingServices);
    const meta = useSelector(getGroomingMeta);
    const loading = useSelector(getGroomingLoading);
    const hasInitialized = useSelector(getGroomingHasInitialized);
    const isTrulyEmpty = useSelector(getGroomingIsTrulyEmpty);
    const hasEverHadServices = useSelector(getGroomingHasEverHadServices);

    // Fetch grooming + dental services
    const fetchGroomingServices = useCallback(
        (
            options: {
                page?: number;
                status?: string;
                search?: string;
                pageSize?: number;
                marketplaceIds?: Array<number>;
            } = {}
        ) => {
            const {
                page = DEFAULT_PAGE,
                status = 'active',
                search = '',
                pageSize = DEFAULT_PAGE_SIZE,
                marketplaceIds
            } = options;

            // Dispatch with grooming business area (includes dental)
            // fetchServicesThunk(duplicatedId?, page?, per?, status?, dir?, searchText?, businessArea?, marketplaceIds?)
            // Note: Using default businessArea (grooming,dental) by passing undefined
            return dispatch(
                fetchServicesThunk(
                    undefined, // duplicatedId
                    page,
                    pageSize,
                    status,
                    'asc', // dir - default sort direction
                    search,
                    undefined, // businessArea defaults to 'grooming,dental'
                    marketplaceIds?.length ? marketplaceIds : undefined
                )
            );
        },
        [dispatch]
    );

    // Toggle service status with targeted refresh
    const toggleGroomingServiceStatus = useCallback(
        (
            serviceIds: Array<number>,
            active: boolean,
            currentFilters: { page?: number; status?: string; search?: string; pageSize?: number }
        ) => {
            const onSuccessCallback = () => {
                // Refresh services with current filters after status change
                fetchGroomingServices(currentFilters);
            };

            return dispatch(activateServicesThunk(serviceIds, active, onSuccessCallback));
        },
        [dispatch, fetchGroomingServices]
    );

    // Delete services with targeted refresh
    const deleteGroomingServices = useCallback(
        (
            serviceIds: Array<number>,
            currentFilters: { page?: number; status?: string; search?: string; pageSize?: number }
        ) => {
            // Note: deleteServicesThunk doesn't accept callback, so we'll need to listen to status changes
            return dispatch(deleteServicesThunk(serviceIds));
        },
        [dispatch]
    );

    // Convenience functions for single service operations
    const toggleSingleGroomingServiceStatus = useCallback(
        (
            serviceId: number,
            active: boolean,
            currentFilters: { page?: number; status?: string; search?: string; pageSize?: number }
        ) => {
            return toggleGroomingServiceStatus([serviceId], active, currentFilters);
        },
        [toggleGroomingServiceStatus]
    );

    const deleteSingleGroomingService = useCallback(
        (serviceId: number, currentFilters: { page?: number; status?: string; search?: string; pageSize?: number }) => {
            return deleteGroomingServices([serviceId], currentFilters);
        },
        [deleteGroomingServices]
    );

    return {
        // Data
        services,
        meta,
        loading,
        hasInitialized,
        isTrulyEmpty,
        hasEverHadServices,

        // Actions (bulk)
        fetchGroomingServices,
        toggleGroomingServiceStatus,
        deleteGroomingServices,

        // Actions (single service convenience)
        toggleSingleGroomingServiceStatus,
        deleteSingleGroomingService
    };
};
